// Shared derivations for the activity-log row + expanded detail panel + toolbar
// filters: the human-readable target name, the short reference codes, the
// module category / entity labels, the action-chip metadata, and the split date
// parts. Kept out of the column/panel/page files so all render from one source.

import i18n from '@/i18n/config';
import { ActivityLogAction, type ActivityItem } from '@/api/endpoints/activityLogApi';
import type { ChipVariant } from '@/components/common';

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Action slug → chip label + tone (also the Action filter's options). The
 *  record is COMPLETE over the confirmed backend enum, so TS flags any new
 *  member the backend adds. */
export const ACTION_META: Record<ActivityLogAction, { labelKey: string; variant: ChipVariant }> = {
  [ActivityLogAction.VIEW]: { labelKey: 'activity_log_action_view', variant: 'neutral' },
  [ActivityLogAction.CREATE]: { labelKey: 'activity_log_action_create', variant: 'success' },
  [ActivityLogAction.EDIT]: { labelKey: 'activity_log_action_edit', variant: 'info' },
  [ActivityLogAction.DELETE]: { labelKey: 'activity_log_action_delete', variant: 'danger' },
  [ActivityLogAction.EXPORT]: { labelKey: 'activity_log_action_export', variant: 'purple' },
  [ActivityLogAction.PUBLISH]: { labelKey: 'activity_log_action_publish', variant: 'success' },
  [ActivityLogAction.APPROVE]: { labelKey: 'activity_log_action_approve', variant: 'success' },
  [ActivityLogAction.ASSIGN]: { labelKey: 'activity_log_action_assign', variant: 'brand' },
  [ActivityLogAction.ASSIGN_ACROSS_INSPECTIONS]: {
    labelKey: 'activity_log_action_assign_across_inspections',
    variant: 'brand',
  },
  [ActivityLogAction.ASSIGN_ACROSS_TASKS]: {
    labelKey: 'activity_log_action_assign_across_tasks',
    variant: 'brand',
  },
  [ActivityLogAction.ASSIGN_ACROSS_TICKETS]: {
    labelKey: 'activity_log_action_assign_across_tickets',
    variant: 'brand',
  },
  [ActivityLogAction.ASSIGN_ACROSS_PROJECTS]: {
    labelKey: 'activity_log_action_assign_across_projects',
    variant: 'brand',
  },
  [ActivityLogAction.RESOLVE]: { labelKey: 'activity_log_action_resolve', variant: 'success' },
  [ActivityLogAction.SHARING]: { labelKey: 'activity_log_action_sharing', variant: 'info' },
  [ActivityLogAction.IMPORT]: { labelKey: 'activity_log_action_import', variant: 'purple' },
  [ActivityLogAction.INVITE]: { labelKey: 'activity_log_action_invite', variant: 'brand' },
  [ActivityLogAction.DEACTIVATE]: {
    labelKey: 'activity_log_action_deactivate',
    variant: 'warning',
  },
  [ActivityLogAction.SETUP]: { labelKey: 'activity_log_action_setup', variant: 'neutral' },
  [ActivityLogAction.FORM_STRUCTURE]: {
    labelKey: 'activity_log_action_form_structure',
    variant: 'neutral',
  },
  [ActivityLogAction.COMPANY_SQL]: {
    labelKey: 'activity_log_action_company_sql',
    variant: 'neutral',
  },
  [ActivityLogAction.ATTENDANCE]: {
    labelKey: 'activity_log_action_attendance',
    variant: 'neutral',
  },
  [ActivityLogAction.TASKS]: { labelKey: 'activity_log_action_tasks', variant: 'neutral' },
  [ActivityLogAction.PEOPLE]: { labelKey: 'activity_log_action_people', variant: 'neutral' },
  [ActivityLogAction.POWERBI_VIEW]: {
    labelKey: 'activity_log_action_powerbi_view',
    variant: 'neutral',
  },
  [ActivityLogAction.FILL]: { labelKey: 'activity_log_action_fill', variant: 'info' },
  [ActivityLogAction.CLOCK_IN]: { labelKey: 'activity_log_action_clock_in', variant: 'success' },
  [ActivityLogAction.CLOCK_OUT]: { labelKey: 'activity_log_action_clock_out', variant: 'warning' },
  [ActivityLogAction.BREAK_START]: {
    labelKey: 'activity_log_action_break_start',
    variant: 'neutral',
  },
  [ActivityLogAction.BREAK_END]: {
    labelKey: 'activity_log_action_break_end',
    variant: 'neutral',
  },
  [ActivityLogAction.STATUS_CHANGED]: {
    labelKey: 'activity_log_action_status_changed',
    variant: 'info',
  },
};

/** Module slug (as sent on the wire) → target tile icon. Unknown modules fall
 *  back to each surface's own glyph. */
export const MODULE_ICONS: Record<string, string> = {
  projects: 'folder-kanban',
  forms_templates: 'file-text',
  people: 'users',
  groups: 'users-round',
  group_section: 'layers',
  data_sources: 'database',
  data_structure: 'library',
  roles: 'shield-check',
  company_structure: 'git-branch',
  inspections: 'clipboard-list',
  attendance: 'clock',
  submissions: 'file-check',
  reports: 'share-2',
  workflow: 'workflow',
  tags: 'tag',
  tasks: 'list-checks',
  tickets: 'ticket',
};

// Module slug → i18n key for its category label (the middle of the meta line,
// e.g. "Roles & access"). Unmapped modules fall back to the slug itself.
const CATEGORY_KEYS: Record<string, string> = {
  roles: 'activity_log_category_roles',
  company_structure: 'activity_log_category_structure',
  inspections: 'activity_log_category_inspections',
  attendance: 'activity_log_category_attendance',
  data_sources: 'activity_log_category_data_sources',
  data_structure: 'activity_log_category_data_structure',
  tags: 'activity_log_category_tags',
  projects: 'activity_log_category_projects',
  submissions: 'activity_log_category_submissions',
  forms_templates: 'activity_log_category_forms',
  workflow: 'activity_log_category_workflow',
  groups: 'activity_log_category_groups',
  group_section: 'activity_log_category_group_section',
  reports: 'activity_log_category_reports',
  people: 'activity_log_category_people',
  tasks: 'activity_log_category_tasks',
};

// Module slug → i18n key for the singular entity label shown in the target
// subtitle ("Role · ROLE-03"). Unmapped modules fall back to the slug.
const ENTITY_KEYS: Record<string, string> = {
  roles: 'activity_log_entity_roles',
  company_structure: 'activity_log_entity_structure',
  inspections: 'activity_log_entity_inspections',
  attendance: 'activity_log_entity_attendance',
  data_sources: 'activity_log_entity_data_sources',
  data_structure: 'activity_log_entity_data_structure',
  tags: 'activity_log_entity_tags',
  projects: 'activity_log_entity_projects',
  submissions: 'activity_log_entity_submissions',
  forms_templates: 'activity_log_entity_forms',
  workflow: 'activity_log_entity_workflow',
  groups: 'activity_log_entity_groups',
  group_section: 'activity_log_entity_group_section',
  reports: 'activity_log_entity_reports',
  people: 'activity_log_entity_people',
  tasks: 'activity_log_entity_tasks',
};

// Module slug → prefix for the target reference code. Unmapped modules derive a
// prefix from the slug.
const ENTITY_CODE_PREFIX: Record<string, string> = {
  roles: 'ROLE',
  company_structure: 'ND',
  inspections: 'INSP',
  attendance: 'ATT',
  data_sources: 'DS',
  data_structure: 'DST',
  tags: 'TAG',
  projects: 'PRJ',
  submissions: 'SUB',
  forms_templates: 'FORM',
  workflow: 'WF',
  groups: 'GRP',
  group_section: 'SEC',
  reports: 'REP',
  people: 'PPL',
  tasks: 'TSK',
};

/** Every module slug the log knows about — the Module filter's option ids,
 *  labelled via `categoryLabel`. */
export const MODULE_SLUGS = Object.keys(CATEGORY_KEYS);

export const categoryLabel = (module: string, t: Translate): string =>
  CATEGORY_KEYS[module] ? t(CATEGORY_KEYS[module]) : module;

export const entityLabel = (module: string, t: Translate): string =>
  ENTITY_KEYS[module] ? t(ENTITY_KEYS[module]) : module;

// The target reference derived from the (hex) entity id — the design shows
// "ROLE-03"; there is no separate sequence for the target, so the tail of its id
// gives a compact handle.

export const targetCode = (module: string, referenceNumber: string, name?: string): string => {
  // Projects code by the project title's initials — the design shows
  // "Q4 Fire Safety Sweep" as "QFS-091". Up to three word-initials, Latin
  // letters/digits only; anything else (e.g. an Arabic title) falls back to the
  // fixed PRJ prefix. The number pads to the design's three digits.
  if (module === 'projects') {
    const initials = (name ?? '')
      .split(/\s+/)
      .map((word) => word[0])
      .filter((ch) => ch && /[A-Za-z0-9]/.test(ch))
      .slice(0, 3)
      .join('')
      .toUpperCase();
    const prefix = initials.length >= 2 ? initials : ENTITY_CODE_PREFIX[module];
    return `${prefix}-${referenceNumber.padStart(3, '0')}`;
  }
  const prefix = ENTITY_CODE_PREFIX[module] ?? module.slice(0, 4).toUpperCase();
  // The inspections design zero-pads its codes to two digits ("INSP-01").
  const padded = module === 'inspections' ? referenceNumber.padStart(2, '0') : referenceNumber;
  return `${prefix}-${padded}`;
};

// The primary target label: the entity's own name. Prefer an explicit name-like
// field from `details` (each module snapshots its own — name/title/fieldTitle/
// schemaName), else pull the quoted name out of the wire sentence ("Updated role
// \"Field Manager\""), else the whole sentence.
export const targetName = (item: ActivityItem): string => {
  const named =
    item.details?.name ??
    item.details?.title ??
    item.details?.fieldTitle ??
    item.details?.schemaName ??
    item.details?.groupName ??
    item.details?.sectionName;
  if (typeof named === 'string' && named) return named;
  const quoted = item.description?.match(/"([^"]+)"/);
  return quoted ? quoted[1] : item.description;
};

// "2026-08-13 09:41" — the full timestamp on the meta line.
export const fmtFullDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${date} ${time}`;
};

// The row's date cell is two lines — the day ("Today" / "8 Aug 2026") over the
// time ("09:41"). Day boundaries are local-time midnights (peopleColumns idiom).
export const activityDateParts = (iso: string, t: Translate): { day: string; time: string } => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: iso, time: '' };
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (d.getTime() >= startOfToday.getTime()) return { day: t('activity_log_day_today'), time };
  if (d.getTime() >= startOfToday.getTime() - 86_400_000)
    return { day: t('activity_log_day_yesterday'), time };
  const day = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
  return { day, time };
};
